/**
 * <AdminInboxCard /> — Dashboard tile listing personal messages the admin
 * team has sent the user (via /admin/users/{uid}/message). Auto-marks each
 * message read once opened.
 */
import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Mailbox, Loader2, ChevronRight } from "lucide-react";

export default function AdminInboxCard() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // opened message id

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/users/me/admin-messages");
      setItems(data.messages || []);
      setUnread(data.unread || 0);
    } catch { /* silently — probably nothing there */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openMsg = async (m) => {
    setOpen(m.id === open ? null : m.id);
    if (!m.read) {
      try { await http.post(`/users/me/admin-messages/${m.id}/read`); }
      catch { /* ignore */ }
      setItems((cur) => cur.map((x) => x.id === m.id ? { ...x, read: true } : x));
      setUnread((n) => Math.max(0, n - 1));
    }
  };

  if (!loading && items.length === 0) return null;

  return (
    <div
      data-testid="admin-inbox-card"
      className="wofy-card"
      style={unread > 0 ? { borderColor: "var(--wofy-primary)", borderWidth: 2, background: "linear-gradient(135deg, #FEF3C7 0%, #FFE4E6 100%)" } : {}}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline" style={{ color: "var(--wofy-primary)" }}>
            <Mailbox size={12} className="inline -mt-0.5 mr-1" /> INBOX FROM SOCIAL PULSE
          </div>
          <h2 className="font-display text-xl mt-1">
            Messages from the team
            {unread > 0 && (
              <span className="ml-2 text-xs rounded-full px-2 py-0.5 text-white" style={{ background: "var(--wofy-primary)" }} data-testid="admin-inbox-unread-badge">
                {unread} new
              </span>
            )}
          </h2>
        </div>
      </div>
      {loading ? (
        <div className="py-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : (
        <div className="space-y-2" data-testid="admin-inbox-list">
          {items.slice(0, 5).map((m) => {
            const isOpen = open === m.id;
            return (
              <div key={m.id} className="rounded-lg border" style={{ borderColor: "var(--wofy-line)", background: m.read ? "transparent" : "rgba(178,93,56,0.05)" }} data-testid={`admin-inbox-msg-${m.id}`}>
                <button
                  type="button"
                  onClick={() => openMsg(m)}
                  className="w-full flex items-center gap-3 p-3 text-left"
                  data-testid={`admin-inbox-open-${m.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm ${m.read ? "" : "font-bold"}`}>{m.subject}</div>
                    <div className="text-xs opacity-70 truncate">
                      {new Date(m.created_at).toLocaleDateString()}
                      {m.from_admin_name ? ` · from ${m.from_admin_name}` : ""}
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 text-sm whitespace-pre-wrap" data-testid={`admin-inbox-body-${m.id}`}>
                    {m.body}
                    <div className="text-xs mt-2 opacity-60">
                      Reply to <a href="mailto:support@socialpulse.com" className="underline">support@socialpulse.com</a>.
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {items.length > 5 && (
            <p className="text-xs text-center pt-1 opacity-70">Showing latest 5 of {items.length}</p>
          )}
        </div>
      )}
    </div>
  );
}
